import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { WhatsApp, ArrowRight, CheckCircle } from '../components/icons';
import ProductCard from '../components/ProductCard';
import { enquiryLink } from '../lib/enquiry';
import { api, API_BASE_URL } from '../lib/api';
import { getProductImage, productImageMap, productImages } from '../assets/products';

const API_ORIGIN = API_BASE_URL.replace(/\/api\/v1\/?$/, '');
const resolveMedia = (url?: string | null): string | null => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/uploads')) return `${API_ORIGIN}${url}`;
  return null;
};

interface ProductVariant { id: string; sku: string; attributes: Record<string, any>; image?: string; [k: string]: any }

// Structured, Ivoclar-style page content (stored in Product.content).
interface ProductContent {
  tagline?: string;
  headline?: string;
  intro?: string;
  benefits?: { title: string; points: string[] }[];
  indications?: string[];
  ivoclarUrl?: string;
}

interface Product {
  id: string;
  name: string;
  description: string;
  shortDescription: string;
  sku: string;
  content?: ProductContent | null;
  category: { id: string; name: string };
  manufacturer: { id: string; name: string };
  variants: ProductVariant[];
  media: Array<{ id: string; url: string; alt?: string; type: string }>;
}

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [mainImage, setMainImage] = useState<string>('');

  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); }, [id]);

  const { data: productResponse, isLoading, error } = useQuery({
    queryKey: ['product', id],
    queryFn: async () => (await api.get(`/products/${id}`)).data,
    enabled: !!id,
  });
  const product: Product | null = productResponse?.data || null;

  const { data: allResponse } = useQuery({
    queryKey: ['products-all-for-related'],
    queryFn: async () => (await api.get('/products')).data,
    enabled: !!product,
  });
  const related = ((allResponse?.data || []) as any[])
    .filter(p => p.id !== product?.id && p.category?.id === product?.category?.id)
    .slice(0, 3);

  const getMainProductImage = () => {
    if (!product) return getProductImage('default');
    const uploaded = (product.media || []).map(m => resolveMedia(m.url)).find(Boolean)
      || (product.variants || []).map(v => resolveMedia(v.image)).find(Boolean);
    if (uploaded) return uploaded;
    const slug = product.name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    if (slug.includes('optragate')) return getProductImage('optragate');
    if (slug.includes('proxem')) return getProductImage('proxem');
    if (slug.includes('cention')) return getProductImage('cention-n');
    for (const [key, value] of Object.entries(productImageMap)) {
      if (slug.includes(key) || product.name.toLowerCase().includes(key)) return getProductImage(value);
    }
    return getProductImage('default');
  };

  const resolveVariantImage = (variant: ProductVariant) => {
    if (!product) return getProductImage('default');
    const real = resolveMedia(variant.image);
    if (real) return real;
    if (variant.image?.startsWith('/images/products/')) {
      const filename = variant.image.split('/').pop();
      if (filename) {
        const key = filename.replace('.png', '').replace(/\s+/g, '-').toLowerCase();
        for (const [k, v] of Object.entries(productImageMap)) if (k.includes(key) || key.includes(k)) return getProductImage(v);
        for (const [k, v] of Object.entries(productImages)) if (k.toLowerCase().includes(key) || key.includes(k.toLowerCase())) return v as string;
      }
    }
    if (variant.image?.startsWith('http')) return variant.image;
    return getMainProductImage();
  };

  const gallery: string[] = (() => {
    if (!product) return [];
    const out: string[] = []; const seen = new Set<string>();
    const add = (u?: string | null) => { if (u && !seen.has(u)) { out.push(u); seen.add(u); } };
    (product.media || []).forEach(m => add(resolveMedia(m.url)));
    (product.variants || []).forEach(v => add(resolveMedia(v.image)));
    if (out.length === 0) add(getMainProductImage());
    return out;
  })();

  useEffect(() => { if (product) setMainImage(getMainProductImage()); /* eslint-disable-next-line */ }, [product]);

  const variantName = (variant: ProductVariant) => {
    const a = variant.attributes || {};
    return a.name || (a.size ? `${a.size}` : '') || a.type || a.form || (a.shade ? `${a.shade} shade` : '') || a.model || variant.sku || 'Standard pack';
  };
  const handleEnquire = () => { if (product) window.open(enquiryLink(product.name), '_blank', 'noopener'); };

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-white"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0D56A6]" /></div>;
  }
  if (error || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4 text-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Product not found</h2>
          <p className="text-gray-500 mb-6">This product isn’t in the catalogue.</p>
          <button onClick={() => navigate('/catalogue')} className="inline-flex items-center gap-2 bg-[#0D56A6] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#0A4A8F]">Back to catalogue <ArrowRight size={16} /></button>
        </div>
      </div>
    );
  }

  const c: ProductContent = product.content || {};
  const tagline = c.tagline || product.shortDescription || '';
  const intro = c.intro || product.description || '';
  const benefits = c.benefits || [];
  const indications = c.indications || [];
  const hasPacks = product.variants && product.variants.length > 1;

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
        <nav className="flex items-center gap-2 text-sm text-gray-500">
          <Link to="/" className="hover:text-gray-900">Home</Link><span>/</span>
          <Link to="/catalogue" className="hover:text-gray-900">Catalogue</Link><span>/</span>
          <span className="text-gray-900 font-medium truncate max-w-[55vw]">{product.name}</span>
        </nav>
      </div>

      {/* ── HERO: name + tagline left, large image right ── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 lg:pt-14 lg:pb-24">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-2 order-2 lg:order-1">
            <div className="flex items-center flex-wrap gap-2 mb-5">
              <Link to="/catalogue" className="px-2.5 py-1 rounded-full bg-[#0D56A6]/10 text-[#0D56A6] text-xs font-semibold hover:bg-[#0D56A6]/15">{product.category?.name}</Link>
              {product.manufacturer?.name && <span className="px-2.5 py-1 rounded-full bg-[#8EC641]/15 text-[#4b8b1f] text-xs font-semibold">{product.manufacturer.name}</span>}
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-[#0D56A6] leading-[1.05]">{product.name}</h1>
            {tagline && <p className="text-xl text-gray-500 mt-4 leading-relaxed">{tagline}</p>}
            <div className="mt-8">
              <button onClick={handleEnquire} className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1eb458] text-white font-semibold px-7 py-3.5 rounded-xl transition-colors">
                <WhatsApp size={18} /> Enquire on WhatsApp
              </button>
            </div>
          </div>
          <div className="lg:col-span-3 order-1 lg:order-2">
            <div className="aspect-[4/3] flex items-center justify-center">
              <img src={mainImage || getMainProductImage()} alt={product.name} className="max-w-full max-h-full object-contain" onError={(e) => { (e.target as HTMLImageElement).src = getProductImage('default'); }} />
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-3 mt-2 justify-center flex-wrap">
                {gallery.map((img, i) => (
                  <button key={i} onClick={() => setMainImage(img)} className={`w-16 h-16 rounded-xl overflow-hidden border-2 bg-gray-50 flex-shrink-0 ${mainImage === img ? 'border-[#0D56A6]' : 'border-gray-200 hover:border-gray-300'}`}>
                    <img src={img} alt="" className="w-full h-full object-contain p-1.5" onError={(e) => { (e.target as HTMLImageElement).src = getProductImage('default'); }} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Benefit headline + intro ── */}
      {(c.headline || intro) && (
        <section className="bg-gray-50/70 border-y border-gray-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0D56A6] leading-tight">
                {c.headline || 'About this product'}
              </h2>
              {intro && <p className="text-lg text-gray-600 leading-relaxed">{intro}</p>}
            </div>
          </div>
        </section>
      )}

      {/* ── Your benefits ── */}
      {benefits.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <p className="text-sm font-bold uppercase tracking-wide text-[#8EC641] mb-10">Your benefits</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
            {benefits.map((b, i) => (
              <div key={i}>
                <div className="w-12 h-12 rounded-full border-2 border-[#0D56A6]/30 flex items-center justify-center text-[#0D56A6] mb-4">
                  <CheckCircle size={22} />
                </div>
                <h3 className="text-lg font-bold text-[#0D56A6] mb-3">{b.title}</h3>
                <ul className="space-y-2">
                  {b.points.map((p, j) => (
                    <li key={j} className="flex items-start gap-2 text-gray-600 text-sm leading-relaxed">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#8EC641] mt-2 flex-shrink-0" />{p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Indications ── */}
      {indications.length > 0 && (
        <section className="bg-gray-50/70 border-y border-gray-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <h2 className="text-2xl font-bold text-[#0D56A6] mb-6">Indications</h2>
            <div className="flex flex-wrap gap-2.5">
              {indications.map((ind, i) => (
                <span key={i} className="px-4 py-2 rounded-full bg-white border border-gray-200 text-sm text-gray-700">{ind}</span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Pack presentations + Ivoclar link ── */}
      {(hasPacks || c.ivoclarUrl) && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid md:grid-cols-2 gap-10">
            {hasPacks && (
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-3">Available pack presentations</h3>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map(v => <span key={v.id} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 bg-gray-50">{variantName(v)}</span>)}
                </div>
                <p className="text-xs text-gray-400 mt-2">Ask us which presentation suits your workflow.</p>
              </div>
            )}
            {c.ivoclarUrl && (
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-3">Full product science</h3>
                <a href={c.ivoclarUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#0D56A6] font-medium hover:underline">
                  Read the details on Ivoclar <ArrowRight size={16} />
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Enquiry band ── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <div className="rounded-2xl bg-[#062f63] text-white p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h3 className="text-xl font-bold">Want to know if it’s right for your cases?</h3>
            <p className="text-white/70 mt-1">Our team will help you choose the right material and pack.</p>
          </div>
          <button onClick={handleEnquire} className="inline-flex items-center gap-2 bg-[#8EC641] hover:bg-[#7ab535] text-white font-semibold px-6 py-3 rounded-xl flex-shrink-0"><WhatsApp size={18} /> Enquire now</button>
        </div>
      </section>

      {/* ── Related products ── */}
      {related.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-end justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Related products</h2>
            <Link to="/catalogue" className="text-sm font-medium text-[#0D56A6] hover:underline inline-flex items-center gap-1">View all <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((p: any) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
